import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { finalize, map, switchMap, take } from 'rxjs/operators';

import { ModalComponent } from '@finder/shared/components/modal/modal.component';
import { ModalService } from '@finder/shared/components/modal/modal.service';
import { FinderApiConnectorService } from '@finder/shared/services/finder-api-connector/finder-api-connector.service';
// eslint-disable-next-line max-len
import { DiningReservationsConflictModalComponent } from '@finder/features/list/components/dining-reservations-conflict-modal/dining-reservations-conflict-modal.component';
// eslint-disable-next-line max-len
import { DiningReservationsConflictModalService } from '@finder/features/list/services/dining-reservations-conflict-modal/dining-reservations-conflict-modal.service';
import { IDiningConflictValidated, IDiningListReservation } from './dining-list-reservations.interface';
import formatScheduleTime from '@finder/shared/utils/string/formatScheduleTime';

@Injectable()
export class DiningListReservationsService {

    reservations: IDiningListReservation[] = [];
    defaultTitle: string;
    conflictModal: ModalComponent;
    loading = false;
    labels: any;

    constructor(
        private finderApi: FinderApiConnectorService,
        private diningReservationsConflictModalService: DiningReservationsConflictModalService,
        private modalService: ModalService
    ) { }

    init(reservationsData: any) {
        this.reservations = reservationsData.reservations;
        this.labels = reservationsData.labels;
        this.defaultTitle = reservationsData.labels.title;

        if (!this.conflictModal) {
            this.createConflictModal(this.labels);
        }
    }

    disconnect() {
        this.reservations = [];
    }

    getClosestReservation(facilityId: string, restaurantsIds: any[], searchDate: Date = new Date()) {
        return this.reservations.find((reservation) => {
            const reservationDate = new Date(reservation.dateTime);
            const reservationTime = reservationDate.getTime();
            const searchTime = searchDate.getTime();

            return this.belongsToReservation(reservation, facilityId, restaurantsIds) &&
                reservationTime >= searchTime;
        });
    }

    checkConflict(swid: string, date: string, time: string, serviceType: string, offerUrl: URL): Observable<IDiningConflictValidated> {
        this.loading = true;

        return this.finderApi.getConflictingItems(swid, date, time, serviceType, `${swid};type=swid`)
            .pipe(
                switchMap(data => {
                    if (!data.conflict) {
                        return of({
                            redirect: true,
                            url: offerUrl.toString()
                        });
                    }

                    return this.openConflictModal(data.conflict)
                        .pipe(
                            map(() => this.validateConflict(data.conflict, offerUrl)),
                            take(1)
                        );
                }),
                finalize(() => this.loading = false)
            );
    }

    private belongsToReservation(reservation: any, facilityId: string, restaurantsIds: any[]) {
        if (reservation.isDinnerShow) {
            return restaurantsIds.includes(reservation.facilityId);
        }

        return reservation.facilityId === facilityId;
    }

    private validateConflict(conflict: any, offerUrl: URL): IDiningConflictValidated {
        // proceed to reservation module only if the user cancel the current conflict
        const reservationCancelled = this.diningReservationsConflictModalService.isReservationCancelled();

        if (reservationCancelled) {
            const conflictingReservation = {
                name: conflict.assetName,
                time: formatScheduleTime(conflict.time),
                isOwner: conflict.isOwner,
                cancelLink: conflict.cancelLink,
            };

            offerUrl.searchParams.append('conflictingReservation', JSON.stringify(conflictingReservation));
        }

        return {
            redirect: reservationCancelled,
            url: offerUrl.toString()
        };
    }

    private openConflictModal(conflictData) {
        // init with default values
        this.diningReservationsConflictModalService.setUp(conflictData);
        this.conflictModal.config.title = conflictData.cancelEligible ? this.defaultTitle : this.labels?.notCancelableConflictTitle;

        this.conflictModal.open();

        return this.conflictModal.afterDismiss();
    }

    private createConflictModal(labels: any) {
        this.conflictModal = this.modalService.create(
            DiningReservationsConflictModalComponent,
            {
                title: labels.title,
                data: {
                    labels
                }
            }
        );
    }
}
